import { useState, useEffect, useCallback, useRef } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Flag, ArrowLeft, Trash2, Upload, Download, X, Pencil, Building2 } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_admin_token") || ""}`,
});

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newClubId, setNewClubId] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchCourses = useCallback(async () => {
    try {
      const [rc, rl] = await Promise.all([
        fetch(`${API}/admin/courses`, { credentials: "include", headers: AUTH() }),
        fetch(`${API}/admin/clubs`, { credentials: "include", headers: AUTH() }),
      ]);
      if (rc.ok) setCourses(await rc.json());
      if (rl.ok) setClubs(await rl.json());
    } catch { toast.error("Error al cargar campos"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const handleCreate = async () => {
    if (!newName.trim()) return toast.error("Ingresa el nombre del campo");
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/courses`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ name: newName.trim(), club_id: newClubId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success("Campo creado");
      setShowNew(false); setNewName(""); setNewClubId("");
      await fetchCourses();
      setSelected(data);
    } catch (err) { toast.error(err.message); }
    finally { setCreating(false); }
  };

  if (selected) {
    return (
      <CourseEditor
        course={selected}
        clubs={clubs}
        onBack={() => { setSelected(null); fetchCourses(); }}
      />
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="admin-courses-title">
            <Flag className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} /> Campos
          </h1>
          <p className="text-sm text-[#64748B] mt-1">{courses.length} campo{courses.length !== 1 ? "s" : ""} dados de alta en la plataforma</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="new-course-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Campo
        </Button>
      </div>

      {showNew && (
        <Card className="card-default mb-6">
          <CardContent className="p-5 flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 w-full space-y-1">
                <Label className="text-xs">Nombre del campo</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ej: Club de Golf Santa Anita" data-testid="new-course-name" />
              </div>
              <div className="flex-1 w-full space-y-1">
                <Label className="text-xs">Club vinculado (opcional)</Label>
                <select
                  value={newClubId}
                  onChange={(e) => setNewClubId(e.target.value)}
                  className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-[#1B4D3E]"
                  data-testid="new-course-club"
                >
                  <option value="">Global (sin club)</option>
                  {clubs.map((c) => (
                    <option key={c.club_id} value={c.club_id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowNew(false); setNewClubId(""); }}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="create-course-btn">
                {creating ? "Creando..." : "Crear"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="courses-list">
          {courses.map((c) => (
            <Card key={c.course_id} className="card-default hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelected(c)} data-testid={`course-card-${c.course_id}`}>
              <CardContent className="p-5 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="font-bold text-[#0F172A] truncate">{c.name}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">Par {c.par} · {Object.keys(c.tees || {}).length} bola{Object.keys(c.tees || {}).length !== 1 ? "s" : ""}</p>
                  <div className="mt-2">
                    {c.club_name ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-0.5 text-[11px] font-medium" data-testid={`course-club-chip-${c.course_id}`}>
                        <Building2 className="w-3 h-3" /> {c.club_name}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-[#64748B] px-2 py-0.5 text-[11px]" data-testid={`course-club-chip-${c.course_id}`}>
                        Global
                      </span>
                    )}
                  </div>
                </div>
                <Pencil className="w-4 h-4 text-[#94A3B8] shrink-0 ml-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CourseEditor({ course: initial, clubs, onBack }) {
  const [course, setCourse] = useState(initial);
  const [name, setName] = useState(initial.name);
  const [clubId, setClubId] = useState(initial.club_id || "");
  const [holes, setHoles] = useState(initial.holes.map((h) => ({ ...h })));
  const [tees, setTees] = useState(JSON.parse(JSON.stringify(initial.tees || {})));
  const [saving, setSaving] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const teeIds = Object.keys(tees);

  const updateHole = (idx, field, value) => {
    setHoles((prev) => prev.map((h, i) => i === idx ? { ...h, [field]: value } : h));
  };

  const updateTeeYards = (tid, holeNum, value) => {
    setTees((prev) => ({
      ...prev,
      [tid]: { ...prev[tid], yards: { ...(prev[tid].yards || {}), [String(holeNum)]: value } },
    }));
  };

  const updateTeeField = (tid, field, value) => {
    setTees((prev) => ({ ...prev, [tid]: { ...prev[tid], [field]: value } }));
  };

  const addTee = () => {
    const tname = window.prompt("Nombre de la bola (ej. Azul, Blanca, Roja):");
    if (!tname?.trim()) return;
    const tid = tname.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_");
    if (tees[tid]) return toast.error("Esa bola ya existe");
    setTees((prev) => ({ ...prev, [tid]: { name: tname.trim(), course_rating: 0, slope_rating: 113, par: 72, yards: {} } }));
  };

  const removeTee = (tid) => {
    if (!window.confirm(`Eliminar la bola ${tees[tid].name}?`)) return;
    setTees((prev) => { const next = { ...prev }; delete next[tid]; return next; });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanHoles = holes.map((h) => ({
        hole: parseInt(h.hole), par: parseInt(h.par) || 4,
        handicap_index: parseInt(h.handicap_index) || 1, yards: parseInt(h.yards) || 0,
      }));
      const res = await fetch(`${API}/admin/courses/${course.course_id}`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ name, club_id: clubId || null, holes: cleanHoles, tees }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      setCourse(data);
      toast.success("Campo guardado");
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Eliminar el campo "${course.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await fetch(`${API}/admin/courses/${course.course_id}`, {
        method: "DELETE", credentials: "include", headers: AUTH(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success("Campo eliminado");
      onBack();
    } catch (err) { toast.error(err.message); }
  };

  const downloadTemplate = () => {
    const teeCols = teeIds.length ? teeIds.map((t) => tees[t].name.toLowerCase()) : ["negro", "azul", "blanco", "rojo"];
    let csv = `hoyo,par,hcp,${teeCols.join(",")}\n`;
    for (let i = 1; i <= 18; i++) {
      const h = holes.find((x) => x.hole === i) || { par: 4, handicap_index: i };
      csv += `${i},${h.par},${h.handicap_index},${teeCols.map(() => "").join(",")}\n`;
    }
    // Tee-level rows: rating, slope and par per bola (complete course upload).
    csv += `rating,,,${teeCols.map((t) => (teeIds.length ? (tees[teeIds.find((id) => tees[id].name.toLowerCase() === t)]?.course_rating ?? "") : "")).join(",")}\n`;
    csv += `slope,,,${teeCols.map((t) => (teeIds.length ? (tees[teeIds.find((id) => tees[id].name.toLowerCase() === t)]?.slope_rating ?? "") : "")).join(",")}\n`;
    csv += `par,,,${teeCols.map(() => "72").join(",")}\n`;
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tarjeta_campo.csv";
    a.click();
  };

  const handleImportCsv = async () => {
    if (!csvText.trim()) return toast.error("Pega o carga el CSV de la tarjeta");
    setImporting(true);
    try {
      const res = await fetch(`${API}/admin/courses/${course.course_id}/scorecard-csv`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ csv: csvText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al importar");
      toast.success("Tarjeta importada");
      if (data.errors?.length) toast.warning(`${data.errors.length} advertencia(s): ${data.errors[0]}`);
      setCourse(data.course);
      setHoles(data.course.holes.map((h) => ({ ...h })));
      setTees(JSON.parse(JSON.stringify(data.course.tees || {})));
      setShowCsv(false); setCsvText("");
    } catch (err) { toast.error(err.message); }
    finally { setImporting(false); }
  };

  const totalPar = holes.reduce((s, h) => s + (parseInt(h.par) || 0), 0);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#0F172A] mb-4" data-testid="back-to-courses">
        <ArrowLeft className="w-4 h-4" /> Volver a Campos
      </button>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex-1 min-w-[240px] flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label className="text-xs">Nombre del campo</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 font-bold" data-testid="course-name-input" />
          </div>
          <div className="flex-1">
            <Label className="text-xs">Club vinculado</Label>
            <select
              value={clubId}
              onChange={(e) => setClubId(e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-[#1B4D3E]"
              data-testid="course-club-select"
            >
              <option value="">Global (sin club)</option>
              {(clubs || []).map((c) => (
                <option key={c.club_id} value={c.club_id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCsv(true)} data-testid="import-scorecard-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Importar CSV
          </Button>
          <Button variant="outline" onClick={handleDelete} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-course-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="save-course-btn">
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>

      {showCsv && (
        <Card className="card-default mb-6">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-sm text-[#0F172A]">Importar tarjeta completa (CSV)</h3>
              <button onClick={() => setShowCsv(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#64748B] mb-3">
              Columnas: <strong>hoyo, par, hcp</strong> (ventaja) y una columna de distancia por cada bola (ej. negro, azul, blanco, rojo). 18 filas, una por hoyo. Para subir el campo completo, agrega al final filas <strong>rating</strong>, <strong>slope</strong> y <strong>par</strong> (en la columna "hoyo") con el valor de cada bola. Las bolas nuevas del CSV se crean automáticamente.
            </p>
            <div className="flex gap-2 mb-3">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-scorecard-template">
                <Download className="w-3.5 h-3.5 mr-1" /> Descargar plantilla
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="w-3.5 h-3.5 mr-1" /> Cargar archivo
              </Button>
              <input type="file" accept=".csv,text/csv" ref={fileRef} className="hidden"
                onChange={(e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = () => setCsvText(r.result); r.readAsText(f); } }} />
            </div>
            <textarea
              value={csvText} onChange={(e) => setCsvText(e.target.value)}
              placeholder={"hoyo,par,hcp,negro,azul,blanco,rojo\n1,4,7,410,395,380,350\n...\n18,4,11,400,385,370,340\nrating,,,74.1,72.3,71.0,70.5\nslope,,,135,130,128,122\npar,,,72,72,72,72"}
              className="w-full h-40 border border-gray-200 rounded-lg p-3 text-xs font-mono focus:outline-none focus:border-[#1B4D3E]"
              data-testid="scorecard-csv-textarea"
            />
            <div className="flex justify-end mt-3">
              <Button size="sm" onClick={handleImportCsv} disabled={importing} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="confirm-import-scorecard">
                {importing ? "Importando..." : "Importar Tarjeta"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tees (Bolas) */}
      <Card className="card-default mb-6">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-[#0F172A]">Bolas / Tees ({teeIds.length})</h3>
            <Button variant="outline" size="sm" onClick={addTee} data-testid="add-tee-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Agregar Bola
            </Button>
          </div>
          {teeIds.length === 0 ? (
            <p className="text-xs text-[#94A3B8]">Sin bolas configuradas. Agrégalas manualmente o vía CSV.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-[#64748B]">
                    <th className="text-left px-2 py-2">Bola</th>
                    <th className="text-center px-2 py-2">Course Rating</th>
                    <th className="text-center px-2 py-2">Slope</th>
                    <th className="text-center px-2 py-2">Par</th>
                    <th className="px-2 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {teeIds.map((tid) => (
                    <tr key={tid} className="border-b border-gray-50">
                      <td className="px-2 py-1.5">
                        <Input value={tees[tid].name} onChange={(e) => updateTeeField(tid, "name", e.target.value)} className="h-8 text-sm w-32" data-testid={`tee-name-${tid}`} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Input type="number" step="0.1" value={tees[tid].course_rating} onChange={(e) => updateTeeField(tid, "course_rating", e.target.value)} className="h-8 text-sm w-24 text-center mx-auto" data-testid={`tee-rating-${tid}`} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Input type="number" value={tees[tid].slope_rating} onChange={(e) => updateTeeField(tid, "slope_rating", e.target.value)} className="h-8 text-sm w-20 text-center mx-auto" data-testid={`tee-slope-${tid}`} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Input type="number" value={tees[tid].par} onChange={(e) => updateTeeField(tid, "par", e.target.value)} className="h-8 text-sm w-16 text-center mx-auto" data-testid={`tee-par-${tid}`} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Button variant="ghost" size="sm" onClick={() => removeTee(tid)} data-testid={`remove-tee-${tid}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scorecard */}
      <Card className="card-default">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-[#0F172A]">Tarjeta del campo</h3>
            <span className="text-xs font-semibold text-[#1B4D3E]">Par total: {totalPar}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="scorecard-table">
              <thead>
                <tr className="border-b border-gray-200 text-xs text-[#64748B]">
                  <th className="text-center px-2 py-2 w-14">Hoyo</th>
                  <th className="text-center px-2 py-2">Par</th>
                  <th className="text-center px-2 py-2">HCP</th>
                  {teeIds.map((tid) => (
                    <th key={tid} className="text-center px-2 py-2">{tees[tid].name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {holes.map((h, idx) => (
                  <tr key={h.hole} className={`border-b border-gray-50 ${h.hole === 10 ? "border-t-2 border-t-[#1B4D3E]/20" : ""}`}>
                    <td className="text-center px-2 py-1 font-bold text-[#0F172A]">{h.hole}</td>
                    <td className="px-1 py-1">
                      <Input type="number" min="3" max="6" value={h.par} onChange={(e) => updateHole(idx, "par", e.target.value)} className="h-8 w-14 text-center text-sm mx-auto" data-testid={`hole-par-${h.hole}`} />
                    </td>
                    <td className="px-1 py-1">
                      <Input type="number" min="1" max="18" value={h.handicap_index} onChange={(e) => updateHole(idx, "handicap_index", e.target.value)} className="h-8 w-14 text-center text-sm mx-auto" data-testid={`hole-hcp-${h.hole}`} />
                    </td>
                    {teeIds.map((tid) => (
                      <td key={tid} className="px-1 py-1">
                        <Input
                          type="number"
                          value={tees[tid].yards?.[String(h.hole)] ?? ""}
                          onChange={(e) => updateTeeYards(tid, h.hole, e.target.value)}
                          placeholder="-"
                          className="h-8 w-20 text-center text-sm mx-auto"
                          data-testid={`hole-yards-${tid}-${h.hole}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#94A3B8] mt-3">HCP = índice de ventaja del hoyo (1 = más difícil). Las distancias son por bola. Guarda con el botón "Guardar" arriba.</p>
        </CardContent>
      </Card>
    </div>
  );
}
